/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, useCallback, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap, cx, jsx } from '@atlaskit/css';
import { ConfluenceIcon } from '@atlaskit/logo/confluence/icon';
import { FocusIcon } from '@atlaskit/logo/focus/icon';
import { JiraProductDiscoveryIcon } from '@atlaskit/logo/jira-product-discovery/icon';
import { JiraServiceManagementIcon } from '@atlaskit/logo/jira-service-management/icon';
import { JiraIcon } from '@atlaskit/logo/jira/icon';
import { LoomIcon } from '@atlaskit/logo/loom/icon';
import { RovoIcon } from '@atlaskit/logo/rovo/icon';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import { useModal } from '@atlaskit/modal-dialog/hooks';
import Modal from '@atlaskit/modal-dialog/modal-dialog';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import twgLogo from '../../../../assets/twg-logo.svg';
import { useAnalyticsEvents } from '../../../../common/analytics/generated/use-analytics-events';
import { TWG_WAITLIST_URL } from '../../../../constants';
import { useAtlassianUserContext } from '../../../../hooks/use-user-context';
import { messages as navMessages } from '../messages';
import { messages } from './messages';

const styles = cssMap({
	// Figma `765:29798`: borderless and 36px tall (8px around the 20px `Text`
	// line), like the feedback button it replaces. Assumes Log in is the next
	// sibling in the bar's `rightControls` (gap `space.200`, `../index.tsx`);
	// -8px brings the pair to Figma's 8px.
	signUpButton: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		marginInlineEnd: token('space.negative.100'),
		borderRadius: token('radius.medium'),
		backgroundColor: token('color.background.neutral.subtle'),
		whiteSpace: 'nowrap',
		flexShrink: 0,
		transition: token('motion.button.hovered'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.subtle.pressed'),
			transition: token('motion.button.pressed'),
		},
	},
	// Figma `795:15137` pads the card 32px by 40px, leaving 320px of content in
	// the 400px small modal.
	card: {
		position: 'relative',
		paddingBlock: token('space.400'),
		paddingInline: token('space.500'),
		// Full-screen on phones: start below the close button and leave the
		// 288px app stack room down to a 320px viewport.
		'@media not all and (min-width: 30rem)': {
			paddingBlockStart: token('space.800'),
			paddingInline: token('space.200'),
		},
	},
	// Figma has no close button; ADS modals need one.
	closeButton: {
		position: 'absolute',
		insetBlockStart: token('space.200'),
		insetInlineEnd: token('space.200'),
	},
	// The nav bar wordmark's tokens (`BrandHomeLink`).
	wordmark: {
		font: token('font.heading.medium'),
		fontFamily: token('font.family.brand.heading'),
		fontWeight: token('font.weight.regular'),
	},
	// Each tile overlaps the one before it by 8px (Figma: 9px; no 9px token).
	// Positioned so a later tile paints over an earlier one even when the
	// earlier one is rotated, which would otherwise lift it on top.
	stackedTile: {
		display: 'flex',
		position: 'relative',
		marginInlineStart: token('space.negative.100'),
	},
	tiltLeft: {
		transform: 'rotate(-6deg)',
	},
	tiltRight: {
		transform: 'rotate(8deg)',
	},
	// The Rovo logo has no tile of its own; Figma sits it on a white one.
	rovoTile: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.100'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.rovo.background.brand.bold'),
	},
	// Figma draws a bright line; this is the brightest neutral border token.
	rule: {
		flexGrow: 1,
		borderBlockStartWidth: token('border.width'),
		borderBlockStartStyle: 'solid',
		borderBlockStartColor: token('color.border.bold'),
	},
});

// Rendered inside `Modal`, so `useModal()` resolves. Naming the dialog after
// its visible lockup (ADS `no-modal-label`) keeps the accessible name on screen.
const Lockup = (): JSX.Element => {
	const { titleId } = useModal();
	return (
		<Inline space="space.100" alignBlock="center" alignInline="center">
			{/* The nav bar's own logo (`BrandHomeLink`), so both lockups match. */}
			<img src={twgLogo} alt="" width={16} height={16} />
			<Box as="span" id={titleId} xcss={styles.wordmark}>
				<FormattedMessage {...navMessages.teamworkGraphBrand} />
			</Box>
		</Inline>
	);
};

type SignupDialogProps = {
	onClose: () => void;
};

/**
 * The sign-up dialog (Figma `795:15137`): anyone already on an Atlassian app logs
 * in, everyone else joins the waitlist. Exported so the VR story can render it open.
 */
export const SignupDialog = ({ onClose }: SignupDialogProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const { attemptUserLogin } = useAtlassianUserContext();
	const { fireEvent } = useAnalyticsEvents();

	// The nav bar's own login, attributed to this dialog like every login CTA.
	const handleLogInClick = useCallback((): void => {
		fireEvent('ui.button.clicked.twgMicrositeLogin', { location: 'signupDialog' });
		attemptUserLogin();
	}, [fireEvent, attemptUserLogin]);

	const handleJoinWaitlistClick = useCallback((): void => {
		fireEvent('ui.joinWaitlist.clicked.twgMicrositeSignupDialog', {});
	}, [fireEvent]);

	return (
		<Modal width="small" onClose={onClose} shouldScrollInViewport testId="signup-dialog">
			<Box xcss={styles.card}>
				{/* First in the DOM, as ADS asks of a custom close button. */}
				<Box xcss={styles.closeButton}>
					<CloseButton
						label={formatMessage(messages.closeLabel)}
						onClick={onClose}
						testId="signup-dialog"
					/>
				</Box>
				<Stack space="space.400">
					<Lockup />
					<Stack space="space.300">
						<Inline alignInline="center">
							<FocusIcon size="xlarge" />
							<Box as="span" xcss={cx(styles.stackedTile, styles.tiltLeft)}>
								<LoomIcon size="xlarge" />
							</Box>
							<Box as="span" xcss={cx(styles.stackedTile, styles.rovoTile)}>
								<RovoIcon size="medium" />
							</Box>
							<Box as="span" xcss={cx(styles.stackedTile, styles.tiltRight)}>
								<JiraIcon size="xlarge" />
							</Box>
							<Box as="span" xcss={styles.stackedTile}>
								<JiraProductDiscoveryIcon size="xlarge" />
							</Box>
							<Box as="span" xcss={cx(styles.stackedTile, styles.tiltLeft)}>
								<ConfluenceIcon size="xlarge" />
							</Box>
							<Box as="span" xcss={styles.stackedTile}>
								<JiraServiceManagementIcon size="xlarge" />
							</Box>
						</Inline>
						<Text as="p" size="large" align="center">
							<FormattedMessage {...messages.existingUsers} values={{ br: <br /> }} />
						</Text>
						<Button shouldFitContainer onClick={handleLogInClick}>
							<FormattedMessage {...messages.logInWithWorkEmail} />
						</Button>
						<Inline space="space.500" alignBlock="center">
							<Box xcss={styles.rule} />
							<Text size="small" color="color.text.subtle">
								<FormattedMessage {...messages.or} />
							</Text>
							<Box xcss={styles.rule} />
						</Inline>
						<Stack space="space.150">
							{/* LinkButton's screen-reader new-tab hint is English-only. */}
							<LinkButton
								href={TWG_WAITLIST_URL}
								target="_blank"
								rel="noopener noreferrer"
								shouldFitContainer
								onClick={handleJoinWaitlistClick}
							>
								<FormattedMessage {...messages.joinWaitlist} />
							</LinkButton>
							<Text as="p" size="small" color="color.text.subtlest" align="center">
								<FormattedMessage {...messages.waitlistHelper} />
							</Text>
						</Stack>
					</Stack>
				</Stack>
			</Box>
		</Modal>
	);
};

/**
 * The nav bar's "Sign up" button and the dialog it opens. `NavigationBar` renders
 * it for a logged-out visitor under `twg_microsite_signup_experience`, in place of
 * the feedback button and just before "Log in".
 */
export const SignupControl = (): JSX.Element => {
	const [isOpen, setIsOpen] = useState(false);
	const { fireEvent } = useAnalyticsEvents();

	const open = useCallback((): void => {
		fireEvent('ui.button.clicked.twgMicrositeSignup', {});
		setIsOpen(true);
	}, [fireEvent]);

	const close = useCallback((): void => {
		setIsOpen(false);
	}, []);

	return (
		<Fragment>
			<Pressable xcss={styles.signUpButton} onClick={open} testId="signup-control">
				<Text weight="medium">
					<FormattedMessage {...messages.signUp} />
				</Text>
			</Pressable>
			<ModalTransition>{isOpen && <SignupDialog onClose={close} />}</ModalTransition>
		</Fragment>
	);
};
